import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronsUpDown, LogOut, ShieldCheck, UserRound } from 'lucide-react'
import { useNavigate } from 'react-router'

import { logout } from '@/api/auth'
import type { User } from '@/api/types'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'
import { initials } from '@/lib/format'

/** The person at the bottom of the sidebar: account, staff console (staff only), log out. */
export const UserMenu = ({ user }: { user: User }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      queryClient.clear()
      await navigate('/login', { replace: true })
    },
  })
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" className="rounded-xl">
              <Avatar className="size-8">
                <AvatarFallback className="bg-secondary text-xs">{initials(user.name, user.email)}</AvatarFallback>
              </Avatar>
              <span className="grid flex-1 text-left leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs text-muted-foreground">{user.email}</span>
              </span>
              <ChevronsUpDown className="ml-auto size-4 text-muted-foreground" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-60 rounded-xl">
            <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
              {user.email}
            </DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => void navigate('/account')}>
              <UserRound /> Account
            </DropdownMenuItem>
            {user.isStaff && (
              <DropdownMenuItem onSelect={() => void navigate('/admin')}>
                <ShieldCheck /> Staff console
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => signOut.mutate()}>
              <LogOut /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
